/**
 * @enum InputType
 * @description
 * **HTML input type constants** for form field components in the SADIN Angular application.
 * 
 * This enum provides type-safe constants for all standard HTML input types, ensuring
 * consistent usage across form components and preventing typos in input type specifications.
 * Each value corresponds to the standard HTML input type attribute values.
 * 
 * **Usage Context:**
 * - Form field components (InputFieldComponent, etc.)
 * - Dynamic form generation
 * - Type validation and safety
 * - Template binding and property assignment
 * 
 * @example
 * // In component
 * public inputType: InputType = InputType.Text;
 * 
 * @example
 * // In template
 * <input [type]="InputType.Email" />
 * 
 * @example
 * // Dynamic assignment
 * const type = InputType.Number;
 * this.field.type = type;
 */
export enum InputType {
    /**
     * @description Hidden input type for invisible form fields
     * @value 'hidden'
     * @example
     * <input type="hidden" name="token" value="abc123" />
     */
    Hidden = 'hidden',

    /**
     * @description Standard text input for single-line text entry
     * @value 'text'
     * @example
     * <input type="text" placeholder="Enter your name" />
     */
    Text = 'text',

    /**
     * @description Numeric input for number-only values
     * @value 'number'
     * @example
     * <input type="number" min="0" max="100" />
     */
    Number = 'number',

    /**
     * @description Email input with built-in email format validation
     * @value 'email'
     * @example
     * <input type="email" placeholder="user@example.com" />
     */
    Email = 'email',

    /**
     * @description Password input with masked character display
     * @value 'password'
     * @example
     * <input type="password" placeholder="Enter password" />
     */
    Password = 'password',

    /**
     * @description Range slider for numeric value selection
     * @value 'range'
     * @example
     * <input type="range" min="0" max="100" value="50" />
     */
    Range = 'range',
    
    /**
     * @description Search input with search-specific styling and behavior
     * @value 'search'
     * @example
     * <input type="search" placeholder="Search..." />
     */
    Search = 'search',

    /**
     * @description Telephone number input with mobile keyboard optimization
     * @value 'tel'
     * @example
     * <input type="tel" placeholder="+1 (555) 123-4567" />
     */
    Tel = 'tel',

    /**
     * @description Image input for image file selection
     * @value 'image'
     * @example
     * <input type="image" src="button.png" alt="Submit" />
     */
    Image = 'image',

    /**
     * @description URL input with URL format validation
     * @value 'url'
     * @example
     * <input type="url" placeholder="https://example.com" />
     */
    Url = 'url',

    /**
     * @description Color picker input for color selection
     * @value 'color'
     * @example
     * <input type="color" value="#ff0000" />
     */
    Color = 'color',

    /**
     * @description Week input for week-of-year selection
     * @value 'week'
     * @example
     * <input type="week" />
     */
    Week = 'week',

    /**
     * @description Time input for time-of-day selection
     * @value 'time'
     * @example
     * <input type="time" />
     */
    Time = 'time',

    /**
     * @description Month input for month selection
     * @value 'month'
     * @example
     * <input type="month" />
     */
    Month = 'month',

    /**
     * @description Date input for date selection
     * @value 'date'
     * @example
     * <input type="date" />
     */
    Date = 'date',

    /**
     * @description Date and time input for datetime selection
     * @value 'datetime-local'
     * @example
     * <input type="datetime-local" />
     */
    datetimeLocal = 'datetime-local',

    /**
     * @description Reset button to clear form fields
     * @value 'reset'
     * @example
     * <input type="reset" value="Clear Form" />
     */
    Reset = 'reset',

    /**
     * @description Submit button to submit form data
     * @value 'submit'
     * @example
     * <input type="submit" value="Submit" />
     */
    Submit = 'submit',

    /**
     * @description Generic button input for custom actions
     * @value 'button'
     * @example
     * <input type="button" value="Click Me" />
     */
    Button = 'button'
}
